import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { logger } from "../../lib/logger";
import { Building2, Check, X, ArrowRight } from "lucide-react";

export default function OrgInvitesInbox() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [activeInvite, setActiveInvite] = useState(null);
  const [mode, setMode] = useState("merge");
  const [newEmail, setNewEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users/me/org-invites");
      setItems(data || []);
    } catch (err) {
      logger.warn("OrgInvitesInbox: load failed", err);
    }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const open = (i) => {
    setActiveInvite(i);
    setMode("merge");
    setNewEmail("");
  };

  const cancel = () => {
    setActiveInvite(null);
    setNewEmail("");
  };

  const accept = async () => {
    if (!activeInvite) return;
    setBusy(true);
    try {
      const body = { mode };
      if (mode === "detach") body.new_email_for_personal_org = newEmail.trim().toLowerCase();
      await api.post(`/users/me/org-invites/${activeInvite.id}/accept`, body);
      toast.success(t("uinv.accepted"));
      setActiveInvite(null);
      await load();
      if (refresh) await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally { setBusy(false); }
  };

  const decline = async (id) => {
    try {
      await api.post(`/users/me/org-invites/${id}/decline`);
      toast.success(t("uinv.declined"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  if (!items.length) return null;

  return (
    <div className="border border-[var(--brick)] bg-[var(--brick)]/5 p-5" data-testid="org-invites-inbox">
      <div className="flex items-center gap-2 label-tech mb-3 text-[var(--brick)]">
        <Building2 size={14} /> {t("uinv.invitesTitle")}
      </div>
      <div className="space-y-3">
        {items.map((i) => (
          <div key={i.id} className="border border-[var(--ink-200)] bg-[var(--bone)] p-4 flex items-center justify-between gap-3" data-testid={`invite-${i.id}`}>
            <div className="min-w-0">
              <div className="font-display text-base">{i.org?.name || "?"}</div>
              <div className="label-tech">{t("uinv.subscription")}: {i.org?.subscription || "—"}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => decline(i.id)} className="btn-ghost text-xs" data-testid={`decline-${i.id}`}>
                <X size={12} /> {t("uinv.declineBtn")}
              </button>
              <button onClick={() => open(i)} className="btn-primary text-xs" data-testid={`accept-open-${i.id}`}>
                {t("uinv.acceptBtn")} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {activeInvite && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4" onClick={cancel} data-testid="accept-modal">
          <div className="bg-[var(--bone)] border border-[var(--ink)] hard-shadow w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="label-tech">{activeInvite.org?.name}</div>
            <h3 className="font-display text-2xl mt-1">{t("uinv.modalTitle")}</h3>
            <p className="label-tech mt-2">{t("uinv.modalHelp")}</p>

            <div className="mt-5 space-y-3">
              <label className={`block border p-3 cursor-pointer ${mode === "merge" ? "border-[var(--ink)] bg-[var(--surface-alt)]" : "border-[var(--ink-200)]"}`}>
                <div className="flex gap-2 items-start">
                  <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="mode-merge-radio" />
                  <div>
                    <div className="text-sm font-medium">{t("uinv.modeMerge")}</div>
                    <div className="label-tech mt-0.5">{t("uinv.modeMergeHelp")}</div>
                  </div>
                </div>
              </label>
              <label className={`block border p-3 cursor-pointer ${mode === "detach" ? "border-[var(--ink)] bg-[var(--surface-alt)]" : "border-[var(--ink-200)]"}`}>
                <div className="flex gap-2 items-start">
                  <input type="radio" checked={mode === "detach"} onChange={() => setMode("detach")} data-testid="mode-detach-radio" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{t("uinv.modeDetach")}</div>
                    <div className="label-tech mt-0.5">{t("uinv.modeDetachHelp")}</div>
                    {mode === "detach" && (
                      <input
                        type="email"
                        className="input-tech mt-2"
                        placeholder={t("uinv.newEmailPlaceholder")}
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        data-testid="detach-new-email"
                        required
                      />
                    )}
                  </div>
                </div>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={cancel} className="btn-ghost" data-testid="modal-cancel-btn">{t("admin.orgs.cancel")}</button>
              <button onClick={accept} disabled={busy || (mode === "detach" && !newEmail)} className="btn-primary" data-testid="modal-accept-btn">
                <Check size={14} /> {t("uinv.confirmAccept")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
